// The administrator's pages, part two: what is published (taking down, putting back, group and icon, every version) and the groups.

import { ApiFailure, api, get } from './api.ts';
import { addButtons, failed, shell } from './admin-frame.ts';
import { categorySelect, iconPayload, iconPickerHtml, riskHtml, statusChip } from './admin-shared.ts';
import type { AdminCategory, AdminItem, AdminSubmission, CategoryLists, Kind } from './types.ts';
import { ENTRY_KIND_NAME, KIND_NAME, confirmModal, errorBlock, esc, fmtTime, iconHtml, must, onAct, refresh, toast } from './util.ts';

const openCount = async (): Promise<number> => (await get<{ openCount: number }>('/api/admin/submissions?state=open')).openCount;

const ITEM_STATUS: Record<AdminItem['status'], { label: string; css: string }> = { draft: { label: '未发布', css: 'draft' }, live: { label: '公开中', css: 'live' }, down: { label: '已下架', css: 'down' } };
const itemChip = (status: AdminItem['status']) => `<span class="chip ${ITEM_STATUS[status].css}">${ITEM_STATUS[status].label}</span>`;

// ---- every item ----

export async function renderItems(app: HTMLElement): Promise<void> {
  try {
    const [list, open] = await Promise.all([get<{ items: AdminItem[] }>('/api/admin/items'), openCount()]);
    const body = list.items.length
      ? `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>名称</th><th>分类</th><th>公开版本</th><th>状态</th><th>待处理</th></tr></thead><tbody>${list.items
          .map(
            (item) => `<tr class="click" data-act="goto" data-href="#/admin/items/${esc(item.id)}"><td><div class="cell-name">${iconHtml(item.icon, item.kind, 'sm')}<div>${esc(item.name)}
              <div class="dim">${ENTRY_KIND_NAME[item.kind]} · ${esc(item.repo)}${item.path ? ` / ${esc(item.path)}` : ''}</div></div></div></td>
              <td>${item.publishedVersion ? esc(item.category ?? '其他') : '<span class="dim">—</span>'}</td><td>${item.publishedVersion ? esc(item.publishedVersion) : '<span class="dim">—</span>'}</td>
              <td>${itemChip(item.status)}</td><td>${item.openVersions ? `<span class="chip pending">${item.openVersions} 个版本</span>` : '<span class="dim">—</span>'}</td></tr>`,
          )
          .join('')}</tbody></table></div>`
      : '<div class="empty"><b>还没有内容</b>用户提交或你添加的 Skill、Plugin 会出现在这里。</div>';
    shell(app, 'items', open, `<div class="page-head" style="margin-bottom:20px"><div><h1 style="font-size:26px">内容管理</h1><p class="sub">设置分类和图标，下架或重新上架已发布的内容。</p></div>${addButtons}</div>${body}`);
  } catch (thrown) {
    shell(app, 'items', 0, errorBlock(failed(thrown)));
  }
}

// ---- one item ----

let current: { item: AdminItem } | null = null;

export async function renderItemDetail(app: HTMLElement, id: string): Promise<void> {
  try {
    const [data, categories, open] = await Promise.all([
      get<{ item: AdminItem; versions: AdminSubmission[] }>(`/api/admin/items/${encodeURIComponent(id)}`),
      get<CategoryLists>('/api/admin/categories'),
      openCount(),
    ]);
    const { item, versions } = data;
    current = { item };
    const published = item.publishedVersion !== null;
    shell(app, 'items', open, `<a class="back" href="#/admin/items">‹ 返回内容管理</a>
      <div class="detail-head">${iconHtml(item.icon, item.kind, 'lg')}<div class="grow"><h1 style="font-size:24px">${esc(item.name)}</h1><div class="sub" style="margin-top:2px">${esc(item.summary)}</div>
        <div class="meta"><span>${ENTRY_KIND_NAME[item.kind]}</span><span>·</span><span>${esc(item.repo)}${item.path ? ` / ${esc(item.path)}` : ''}</span>${published ? `<span>·</span><span>公开版本 ${esc(item.publishedVersion ?? '')}</span>` : ''}</div></div>
        ${itemChip(item.status)}
        ${published && item.listed ? '<button class="btn-danger" data-act="takedown">下架</button>' : ''}
        ${published && !item.listed ? `<button class="btn-primary" data-act="relist">重新上架 ${esc(item.publishedVersion ?? '')}</button>` : ''}
        ${item.listed ? `<a class="btn" href="#/items/${esc(item.id)}">查看公开页面</a>` : ''}</div>
      ${published && !item.listed ? `<div class="note warn" style="margin-top:20px">已下架：公开页面和 Otoha 都不再提供搜索、获取和下载。已经下载到用户电脑上的内容不受影响。重新上架会恢复之前审核通过的版本 ${esc(item.publishedVersion ?? '')}。</div>` : ''}
      <div class="review-grid"><div class="card"><h3>版本记录</h3><div class="tbl-wrap"><table class="tbl"><thead><tr><th>版本</th><th>提交</th><th>提交者</th><th>状态</th><th>检测结果</th></tr></thead><tbody>
        ${versions
          .map(
            (v) => `<tr class="click" data-act="goto" data-href="#/admin/review/${esc(v.id)}"><td>${esc(v.version)}</td><td class="mono">${esc(v.commit.slice(0, 7))}<div class="dim">${fmtTime(v.submittedAt)}</div></td>
              <td>${v.submittedBy === 'admin' ? '管理员' : '用户'}</td><td>${statusChip(v.status)}</td><td>${riskHtml(v.scanState === 'done' ? v.risk : null)}</td></tr>`,
          )
          .join('')}</tbody></table></div><p class="dim" style="margin:12px 0 0">公开页面和 Otoha 只提供最新发布的版本，历史版本只在这里可见。</p></div>
      <div class="card"><h3>公开展示设置</h3>${
        published
          ? `${item.kind === 'prompt' ? '<input type="hidden" id="item-category" value="">' : `<label class="field"><span>分类</span>${categorySelect('item-category', categories[item.kind], item.categoryId)}</label>`}${iconPickerHtml(item.icon, item.kind)}
             <p class="dim" style="margin:0 0 14px">展示名称和简介在审核新版本时修改。</p><button class="btn-primary" data-act="save-item">保存</button>`
          : '<p class="dim" style="margin:0">发布后可以设置分类和图标。</p>'
      }</div></div>`);
  } catch (thrown) {
    const missing = thrown instanceof ApiFailure && thrown.status === 404;
    shell(app, 'items', 0, missing ? '<div class="empty"><b>找不到这个内容</b><a class="link" href="#/admin/items">返回内容管理</a></div>' : errorBlock(failed(thrown)));
  }
}

const itemId = (): string => encodeURIComponent(current?.item.id ?? '');

onAct('save-item', async () => {
  try {
    await api('PATCH', `/api/admin/items/${itemId()}`, { categoryId: must<HTMLSelectElement>('#item-category').value || null, icon: iconPayload() });
    toast('已保存');
  } catch (thrown) {
    toast(failed(thrown));
  }
  refresh();
});

onAct('takedown', () => {
  const item = current?.item;
  if (!item) return;
  confirmModal({ title: `下架「${item.name}」？`, body: '下架后，公开页面不再展示，Otoha 也不再能搜索、获取和下载它。用户已经下载到电脑上的内容不受影响。之后可以重新上架同一个版本。', ok: '下架', danger: true }, async () => {
    try {
      await api('POST', `/api/admin/items/${itemId()}/takedown`, {});
      toast('已下架');
    } catch (thrown) {
      toast(failed(thrown));
    }
    refresh();
  });
});

onAct('relist', () => {
  const item = current?.item;
  if (!item) return;
  confirmModal({ title: `重新上架「${item.name}」？`, body: `将恢复公开展示之前审核通过的版本 ${esc(item.publishedVersion ?? '')}，Otoha 也可以再次搜索和下载。`, ok: '重新上架' }, async () => {
    try {
      await api('POST', `/api/admin/items/${itemId()}/relist`, {});
      toast(`已重新上架 ${item.publishedVersion ?? ''}`);
    } catch (thrown) {
      toast(failed(thrown));
    }
    refresh();
  });
});

// ---- the groups ----

export async function renderCategories(app: HTMLElement): Promise<void> {
  try {
    const [lists, open] = await Promise.all([get<CategoryLists>('/api/admin/categories'), openCount()]);
    const column = (kind: Kind) => `<div class="card"><h3>${KIND_NAME[kind]}的分类</h3><ul class="cat-list">${lists[kind]
      .map(
        (category, index, all) => `<li><input class="input" data-cat="${esc(category.id)}" value="${esc(category.name)}" aria-label="分类名称" maxlength="40"><span class="dim" style="white-space:nowrap;width:52px">${category.items} 项</span>
          <button class="btn btn-sm" ${index === 0 ? 'disabled' : ''} data-act="cat-move" data-kind="${kind}" data-id="${esc(category.id)}" data-by="-1" title="上移">↑</button>
          <button class="btn btn-sm" ${index === all.length - 1 ? 'disabled' : ''} data-act="cat-move" data-kind="${kind}" data-id="${esc(category.id)}" data-by="1" title="下移">↓</button>
          <button class="btn btn-sm" data-act="cat-delete" data-kind="${kind}" data-id="${esc(category.id)}">删除</button></li>`,
      )
      .join('')}</ul>
      <form data-cat-form="${kind}" style="display:flex;gap:8px;margin-top:12px"><input class="input" name="name" placeholder="新分类名称" maxlength="40" style="height:36px"><button class="btn btn-sm" style="height:36px">添加</button></form>
      <p class="dim" style="margin:12px 0 0">公开页面按这里的顺序分组；没有分类的内容显示在最后的“其他”里。</p></div>`;
    shell(app, 'cats', open, `<div class="page-head" style="margin-bottom:20px"><div><h1 style="font-size:26px">分类</h1><p class="sub">公开列表共用这些分类。做法和整包仍各自归档，避免把两类放进同一个分类。</p></div></div><div class="two">${column('skill')}${column('plugin')}</div>`);
    categoryState = lists;
  } catch (thrown) {
    shell(app, 'cats', 0, errorBlock(failed(thrown)));
  }
}

let categoryState: CategoryLists | null = null;
const found = (kind: Kind, id: string): AdminCategory | undefined => categoryState?.[kind].find((category) => category.id === id);

document.addEventListener('change', async (event) => {
  const input = event.target as HTMLInputElement;
  const id = input.dataset.cat;
  if (!id) return;
  try {
    await api('PATCH', `/api/admin/categories/${encodeURIComponent(id)}`, { name: input.value });
    toast('已更新分类名称');
  } catch (thrown) {
    toast(failed(thrown));
  }
  refresh();
});

document.addEventListener('submit', async (event) => {
  const form = event.target as HTMLFormElement;
  const kind = form.dataset.catForm as Kind | undefined;
  if (!kind) return;
  event.preventDefault();
  const input = form.elements.namedItem('name') as HTMLInputElement;
  if (!input.value.trim()) return;
  try {
    await api('POST', '/api/admin/categories', { type: kind, name: input.value });
    toast(`已添加分类“${input.value.trim()}”`);
  } catch (thrown) {
    toast(failed(thrown));
  }
  refresh();
});

onAct('cat-move', async (element) => {
  const kind = element.dataset.kind as Kind;
  const ids = (categoryState?.[kind] ?? []).map((category) => category.id);
  const from = ids.indexOf(element.dataset.id ?? '');
  const to = from + Number(element.dataset.by);
  if (from < 0 || to < 0 || to >= ids.length) return;
  [ids[from], ids[to]] = [ids[to] as string, ids[from] as string];
  try {
    await api('POST', '/api/admin/categories/reorder', { type: kind, ids });
  } catch (thrown) {
    toast(failed(thrown));
  }
  refresh();
});

onAct('cat-delete', (element) => {
  const kind = element.dataset.kind as Kind;
  const category = found(kind, element.dataset.id ?? '');
  if (!category) return;
  const run = async () => {
    try {
      await api('DELETE', `/api/admin/categories/${encodeURIComponent(category.id)}`);
      toast('已删除分类');
    } catch (thrown) {
      toast(failed(thrown));
    }
    refresh();
  };
  if (category.items > 0) confirmModal({ title: `删除分类“${category.name}”？`, body: `其中的 ${category.items} 项内容会移到“其他”，之后可以重新指定分类。`, ok: '删除', danger: true }, () => void run());
  else void run();
});
